import { useEffect, useRef, useState, useCallback } from 'react';
import * as conn from './wsConnection';

export type EventType =
  | 'project.created'
  | 'project.deleted'
  | 'project.updated'
  | 'build.started'
  | 'build.completed'
  | 'build.failed'
  | 'agent.started'
  | 'agent.stopped'
  | 'agent.output'
  | 'image.op.started'
  | 'image.op.progress'
  | 'image.op.completed'
  | 'image.op.failed';

export interface WSEvent {
  type: EventType;
  timestamp: string;
  data: unknown;
}

interface UseWebSocketOptions {
  /** Called for each parsed WSEvent (batched frames are split automatically) */
  onEvent?: (event: WSEvent) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
}

// Subscribes to the tab's shared WebSocket (see wsConnection.ts) for the
// lifetime of the component. Unmounting unsubscribes for good.
export function useWebSocket(options: UseWebSocketOptions = {}) {
  const [isConnected, setIsConnected] = useState(conn.isOpen());
  const [lastEvent, setLastEvent] = useState<WSEvent | null>(null);

  // Latest callbacks without resubscribing when they change.
  const onEventRef = useRef(options.onEvent);
  onEventRef.current = options.onEvent;
  const onConnectRef = useRef(options.onConnect);
  onConnectRef.current = options.onConnect;
  const onDisconnectRef = useRef(options.onDisconnect);
  onDisconnectRef.current = options.onDisconnect;
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const connect = useCallback(() => {
    if (unsubscribeRef.current) return;
    unsubscribeRef.current = conn.subscribe({
      onOpen: () => {
        setIsConnected(true);
        onConnectRef.current?.();
      },
      onClose: () => {
        setIsConnected(false);
        onDisconnectRef.current?.();
      },
      onMessage: (data) => {
        const wsEvent = data as WSEvent;
        setLastEvent(wsEvent);
        onEventRef.current?.(wsEvent);
      },
    });
  }, []);

  const disconnect = useCallback(() => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
  }, []);

  useEffect(() => {
    connect();
    return disconnect;
  }, [connect, disconnect]);

  return {
    isConnected,
    lastEvent,
    connect,
    disconnect,
    send: conn.send,
  };
}
